import { formatRelative, formatUtcDate } from '../lib/format';
import { PROVIDER_LABEL } from '../lib/integrations';
import type { IntegrationProvider } from '../lib/types';
import { Badge } from './ui';

const DAY_MS = 86_400_000;

/** Whether the device is enrolled in Jamf, and which Jamf. */
export function MdmBadge({ device }: { device: { mdmProvider: IntegrationProvider | null; mdmRemovedAt: string | null; managed?: boolean | null } }) {
  if (!device.mdmProvider) return <Badge tone="amber">Not enrolled</Badge>;
  const label = PROVIDER_LABEL[device.mdmProvider];
  if (device.mdmRemovedAt) return <Badge tone="gray">Removed from {label}</Badge>;
  if (device.managed === false) return <Badge tone="amber">{label} · unmanaged</Badge>;
  return <Badge tone="green">{label}</Badge>;
}

/** Apple School Manager assignment. Renders nothing for devices Apple School Manager doesn't list. */
export function AsmBadge({
  device,
}: {
  device: { asmStatus: string | null; asmSeenAt: string | null; asmRemovedAt: string | null; releasedFromOrgAt: string | null; asmMdmServerName: string | null };
}) {
  if (device.releasedFromOrgAt) return <Badge tone="gray">Released</Badge>;
  if (!device.asmSeenAt || device.asmRemovedAt) return <span className="text-xs text-slate-400">Not listed</span>;
  if (device.asmStatus === 'ASSIGNED') return <Badge tone="blue">{device.asmMdmServerName ? `Assigned to ${device.asmMdmServerName}` : 'Assigned'}</Badge>;
  return <Badge tone="amber">Unassigned</Badge>;
}

export function AppleCareBadge({ endsAt, description }: { endsAt: string | null; description?: string | null }) {
  if (!endsAt) return <span className="text-slate-400">—</span>;
  const ms = new Date(endsAt).getTime() - Date.now();
  const label = formatUtcDate(endsAt);
  const title = description ?? undefined;
  if (ms < 0) {
    return (
      <span title={title}>
        <Badge tone="gray">Ended {label}</Badge>
      </span>
    );
  }
  if (ms < 90 * DAY_MS) {
    return (
      <span title={title}>
        <Badge tone="amber">Ends {label}</Badge>
      </span>
    );
  }
  return (
    <span className="text-slate-600" title={title}>
      {label}
    </span>
  );
}

/** Last MDM check-in; highlighted once it's been a month. */
export function CheckInText({ date }: { date: string | null }) {
  if (!date) return <span className="text-slate-400">Never</span>;
  const days = (Date.now() - new Date(date).getTime()) / DAY_MS;
  return <span className={days > 30 ? 'font-medium text-amber-700' : 'text-slate-700'}>{formatRelative(date)}</span>;
}
